import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  StickyNote, CheckCircle2, RotateCcw, ArrowUpRight,
  Loader2, FileText, BookOpen, Filter, Search,
} from 'lucide-react';
import studyApi from '../api/studyProgress';
import { useAuth } from '../hooks/useAuth';

const TYPE_LABEL = { article: 'Article', gk: 'GK Topic' };
const TYPE_COLOR = { article: '#6366F1', gk: '#C9A227' };
const TYPE_BG    = { article: '#EEF2FF', gk: '#FFFBEB' };

function itemUrl(type, slug) {
  if (type === 'article') return `/articles/${slug}`;
  return `/gk/${slug}`; // slug is already "category/topic-slug"
}

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MyNotesPage() {
  const navigate        = useNavigate();
  const { user }        = useAuth();
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');   // all | article | gk
  const [modeFilter, setModeFilter] = useState('notes'); // all | notes | revisit | completed
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    studyApi.all()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = items.filter(it => {
    if (typeFilter !== 'all' && it.itemType !== typeFilter) return false;
    if (modeFilter === 'notes'     && !it.note?.trim()) return false;
    if (modeFilter === 'revisit'   && !it.revisit)      return false;
    if (modeFilter === 'completed' && !it.completed)    return false;
    if (search) {
      const q = search.toLowerCase();
      if (!it.itemTitle?.toLowerCase().includes(q) && !it.note?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const noteCount     = items.filter(i => i.note?.trim()).length;
  const revisitCount  = items.filter(i => i.revisit).length;
  const completedCount = items.filter(i => i.completed).length;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center space-y-3">
          <StickyNote size={40} className="text-gray-300 mx-auto" />
          <p className="text-gray-500 text-sm">Please log in to see your notes.</p>
          <button onClick={() => navigate('/login')}
            className="text-sm font-bold text-indigo-600 hover:underline">
            Log in →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <StickyNote size={16} className="text-yellow-400 flex-shrink-0" />
          <h1 className="font-sora font-black text-gray-900 text-base flex-1">My Study Notes</h1>
          <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
            <span><span className="text-indigo-600 font-bold">{completedCount}</span> done</span>
            <span><span className="text-yellow-500 font-bold">{revisitCount}</span> revisit</span>
            <span><span className="text-blue-500 font-bold">{noteCount}</span> notes</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Search */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 flex-1 min-w-[180px] max-w-xs">
            <Search size={13} className="text-gray-400 flex-shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search topics or notes…"
              className="text-sm text-gray-700 placeholder-gray-400 outline-none w-full bg-transparent"
            />
          </div>

          {/* Type pills */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1">
            {[['all','All'],['article','Articles'],['gk','GK']].map(([v,l]) => (
              <button key={v} onClick={() => setTypeFilter(v)}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                  typeFilter === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'
                }`}>
                {l}
              </button>
            ))}
          </div>

          {/* Mode pills */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1">
            {[
              ['all',      'All items',  null],
              ['notes',    'Notes',      'text-blue-500'],
              ['revisit',  'Revisit',    'text-yellow-500'],
              ['completed','Completed',  'text-green-500'],
            ].map(([v, l, cls]) => (
              <button key={v} onClick={() => setModeFilter(v)}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                  modeFilter === v ? 'bg-gray-900 text-white' : `text-gray-500 hover:text-gray-800`
                }`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-gray-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 space-y-3">
            <StickyNote size={40} className="text-gray-200 mx-auto" />
            <p className="text-gray-400 text-sm">
              {items.length === 0
                ? 'No study activity yet. Mark topics as complete or add notes while reading.'
                : 'No items match your filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {filtered.map((item, idx) => (
                <motion.div
                  key={item._id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ delay: idx * 0.03, duration: 0.18 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  {/* Top accent bar */}
                  <div className="h-0.5 w-full" style={{ background: TYPE_COLOR[item.itemType] }} />

                  <div className="p-4 sm:p-5">
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-2.5 flex-1 min-w-0">
                        {item.itemType === 'article'
                          ? <FileText size={15} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                          : <BookOpen  size={15} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                        }
                        <div className="min-w-0">
                          <p className="font-sora font-bold text-sm text-gray-900 leading-snug truncate">
                            {item.itemTitle || item.itemSlug}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span
                              className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md"
                              style={{ background: TYPE_BG[item.itemType], color: TYPE_COLOR[item.itemType] }}
                            >
                              {TYPE_LABEL[item.itemType]}
                            </span>
                            {item.completed && (
                              <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-600">
                                <CheckCircle2 size={10} /> Completed
                              </span>
                            )}
                            {item.revisit && (
                              <span className="flex items-center gap-0.5 text-[10px] font-bold text-yellow-600">
                                <RotateCcw size={10} /> Revisit
                              </span>
                            )}
                            {item.noteUpdatedAt && (
                              <span className="text-[10px] text-gray-400">{fmtDate(item.noteUpdatedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => navigate(itemUrl(item.itemType, item.itemSlug))}
                        className="flex items-center gap-1 text-[11px] font-bold text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                      >
                        Open <ArrowUpRight size={12} />
                      </button>
                    </div>

                    {/* Note text */}
                    {item.note?.trim() ? (
                      <div
                        className="rounded-xl px-4 py-3 text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap"
                        style={{ background: '#FAFAFA', border: '1px solid #F0F0F0' }}
                      >
                        {item.note}
                      </div>
                    ) : (
                      <p className="text-[12px] text-gray-300 italic">No note written yet</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
